import { useState, useRef, useCallback, useEffect } from "react"
import { DEMO_QUOTA_EVENT } from "@/shared/lib/demoQuota"

export type AudioRecorderState = "idle" | "recording" | "error"

interface UseAudioRecorderOptions {
    onChunk?: (blob: Blob, index: number) => void
    onStop?: (result: { blob: Blob; durationSec: number; totalChunks: number }) => void
    onError?: () => void
}

const CHUNK_TIMESLICE_MS = 3000
const INIT_CAPTURE_MS = 20
// Guards the init-recorder -> real-recorder transition only (armed AFTER the
// microphone is acquired). If the real recorder has not started within this
// window — a cold mic or a stuck init recorder — we surface an explicit error
// instead of silently staying "idle". It does NOT cover the getUserMedia
// permission prompt, which may legitimately take far longer.
const START_TIMEOUT_MS = 6000

function getPreferredMimeType(): string {
    const candidates = ["audio/webm;codecs=opus", "audio/webm"]
    return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? ""
}

export function useAudioRecorder({ onChunk, onStop, onError }: UseAudioRecorderOptions = {}) {
    const [state, setState] = useState<AudioRecorderState>("idle")
    const [time, setTime] = useState(0)
    const [error, setError] = useState<string | null>(null)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const initRecorderRef = useRef<MediaRecorder | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const timerRef = useRef<number | null>(null)
    const chunkIndexRef = useRef(0)
    const timeRef = useRef(0)
    // Synchronous single-flight guard: set BEFORE the async getUserMedia so a
    // second start() in the same tick (double click, re-render) is a no-op.
    // This is the structural fix for the double-recorder incident.
    const startingRef = useRef(false)
    // Generation token: bumped on every start/stop/fail so a getUserMedia that
    // resolves AFTER a cancel cannot bring a ghost recorder back to life.
    const startGenRef = useRef(0)
    const startWatchdogRef = useRef<number | null>(null)
    const onChunkRef = useRef(onChunk)
    const onStopRef = useRef(onStop)
    const onErrorRef = useRef(onError)
    useEffect(() => { onChunkRef.current = onChunk }, [onChunk])
    useEffect(() => { onStopRef.current = onStop }, [onStop])
    useEffect(() => { onErrorRef.current = onError }, [onError])

    const clearWatchdog = useCallback(() => {
        if (startWatchdogRef.current) {
            window.clearTimeout(startWatchdogRef.current)
            startWatchdogRef.current = null
        }
    }, [])

    const cleanup = useCallback(() => {
        clearWatchdog()
        if (timerRef.current) {
            window.clearInterval(timerRef.current)
            timerRef.current = null
        }
        if (initRecorderRef.current) {
            initRecorderRef.current.ondataavailable = null
            initRecorderRef.current.onstop = null
            if (initRecorderRef.current.state !== "inactive") {
                initRecorderRef.current.stop()
            }
            initRecorderRef.current = null
        }
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.onstop = null
            mediaRecorderRef.current.ondataavailable = null
            if (mediaRecorderRef.current.state !== "inactive") {
                mediaRecorderRef.current.stop()
            }
            mediaRecorderRef.current = null
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
        }
    }, [clearWatchdog])

    useEffect(() => {
        return cleanup
    }, [cleanup])

    const failStart = useCallback((message: string) => {
        startGenRef.current++
        clearWatchdog()
        startingRef.current = false
        setState("error")
        setError(message)
        cleanup()
        onErrorRef.current?.()
    }, [cleanup, clearWatchdog])

    const startRecording = useCallback(async () => {
        // Single-flight: ignore concurrent/duplicate starts.
        if (startingRef.current) return
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") return
        startingRef.current = true
        const myGen = ++startGenRef.current

        cleanup()
        setError(null)
        chunksRef.current = []
        chunkIndexRef.current = 0
        timeRef.current = 0
        setTime(0)

        let stream: MediaStream
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch (err) {
            // getUserMedia — browser API, not network (query-exceptions).
            startGenRef.current++
            startingRef.current = false
            setState("error")
            if (err instanceof DOMException) {
                if (err.name === "NotAllowedError" || err.name === "NotFoundError") {
                    setError("Микрофон не найден или доступ запрещен")
                } else {
                    setError("Ошибка доступа к микрофону")
                }
            } else {
                setError("Неизвестная ошибка записи")
            }
            cleanup()
            onErrorRef.current?.()
            return
        }

        // A stop()/new start() happened while we awaited permission — drop this
        // stream instead of starting a recorder nobody asked for any more.
        if (startGenRef.current !== myGen) {
            stream.getTracks().forEach((t) => t.stop())
            return
        }

        streamRef.current = stream
        const mimeType = getPreferredMimeType()
        const recorderOptions = mimeType ? { mimeType } : undefined

        // Arm the stall watchdog only now that the mic is live.
        startWatchdogRef.current = window.setTimeout(() => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") return
            failStart("Не удалось начать запись — попробуйте ещё раз")
        }, START_TIMEOUT_MS)

        // ── Phase 1: short-lived recorder to ensure stream is ready ─────────
        // A 20ms recorder fires immediately, then the real 3s recorder starts.
        // This ensures the main recorder's chunk_0 always contains a full EBML
        // header so the backend can decode it as a standalone WebM file.
        const initRecorder = new MediaRecorder(stream, recorderOptions)
        initRecorderRef.current = initRecorder
        let initCaptured = false

        initRecorder.ondataavailable = (e) => {
            if (!initCaptured && e.data.size > 0) {
                initCaptured = true
                initRecorder.stop()
            }
        }

        initRecorder.onstop = () => {
            initRecorderRef.current = null
            if (startGenRef.current !== myGen) return

            // ── Phase 2: real recorder with 3s timeslice ────────────────────
            const recorder = new MediaRecorder(stream, recorderOptions)
            mediaRecorderRef.current = recorder

            recorder.ondataavailable = (event) => {
                if (event.data.size === 0) return
                chunksRef.current.push(event.data)
                const index = chunkIndexRef.current++
                onChunkRef.current?.(event.data, index)
            }

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
                chunksRef.current = []
                onStopRef.current?.({ blob, durationSec: timeRef.current, totalChunks: chunkIndexRef.current })
            }

            recorder.start(CHUNK_TIMESLICE_MS)
            clearWatchdog()
            startingRef.current = false
            setState("recording")
            timerRef.current = window.setInterval(() => {
                setTime((prev) => {
                    timeRef.current = prev + 1
                    return prev + 1
                })
            }, 1000)
        }

        initRecorder.start(INIT_CAPTURE_MS)
    }, [cleanup, clearWatchdog, failStart])

    const stopRecording = useCallback(() => {
        startGenRef.current++
        clearWatchdog()
        startingRef.current = false
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop()
            setState("idle")
            if (timerRef.current) {
                window.clearInterval(timerRef.current)
                timerRef.current = null
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop())
                streamRef.current = null
            }
        }
    }, [clearWatchdog])

    useEffect(() => {
        const onQuota = () => stopRecording()
        window.addEventListener(DEMO_QUOTA_EVENT, onQuota)
        return () => window.removeEventListener(DEMO_QUOTA_EVENT, onQuota)
    }, [stopRecording])

    const resetError = useCallback(() => {
        if (state === "error") {
            setState("idle")
            setError(null)
        }
    }, [state])

    return { state, time, error, startRecording, stopRecording, resetError }
}
